/// <reference types="vite/client" />
/**
 * PostHog product analytics (browser) — additive, fail-open, and a NO-OP unless
 * a PostHog key is configured. No key → every export below is a silent no-op,
 * so local dev and un-keyed builds are untouched.
 *
 * Key resolution: the runtime-injected `window.__APP_ENV__.posthogKey` first
 * (served by `GET /v2/env.js` — the Nixpacks build bakes no VITE_* vars, so
 * build-time env is dead in prod), then the build-time fallback:
 *   VITE_POSTHOG_KEY          PostHog PRODUCT project key (browser).
 *   VITE_POSTHOG_COMPANY_KEY  PostHog COMPANY project key (optional dual-send;
 *                             build-time only for now — not in the documented
 *                             runtime whitelist).
 *
 * Populus CoreOS product-analytics convention (US region):
 *   - Events ride the app's OWN origin via the `/ingest` reverse proxy
 *     (see src/routes/analytics-ingest.js) so ad/tracker blockers don't drop
 *     them. `api_host: '/ingest'`, `ui_host: 'https://us.posthog.com'`.
 *   - Every event carries a `product: 'ignite'` super-property (registered
 *     once at init) + the `tenant` group when the org id is known.
 *   - A small TOP-LINE (★) subset ALSO dual-sends to a shared COMPANY project
 *     (`VITE_POSTHOG_COMPANY_KEY`, optional) for the cross-product dashboard.
 *   - NO PII in event properties — ids/counts/statuses/enums/hashes only.
 *     Identity flows through identify()/group(), not props.
 *
 * Fail-open: every call is wrapped so an analytics error can never break a
 * render or a user action. Off the critical path.
 */

import posthog, { type PostHog } from 'posthog-js';
import { appEnv } from './app-env';

const PRODUCT = 'ignite';
const UI_HOST = 'https://us.posthog.com';

/** Top-line ★ events that also go to the shared company project. */
const TOP_LINE_EVENTS = new Set<string>([
  'account_created',
  'activated',
  'appeal_filed',
  'revenue_event',
]);

let product: PostHog | null = null;
let company: PostHog | null = null;
let ready = false;

/**
 * Initialize PostHog. No-op unless VITE_POSTHOG_KEY is set. Safe to call once
 * at app boot (idempotent — repeated calls are ignored).
 */
export function initAnalytics(): void {
  if (ready) return;
  // Runtime-injected key first (GET /v2/env.js → window.__APP_ENV__; the
  // Nixpacks build bakes no VITE_* vars so build-time env is dead in prod),
  // build-time VITE_ var as the local-dev fallback.
  const key = appEnv().posthogKey || import.meta.env.VITE_POSTHOG_KEY;
  if (!key) return; // no key → analytics disabled (no-op)

  try {
    product = posthog.init(key, {
      api_host: '/ingest',
      ui_host: UI_HOST,
      // We drive pageviews manually on route changes (SPA); autocapture stays on
      // for clicks/inputs but is safe (no PII in props by default here).
      capture_pageview: false,
      capture_pageleave: true,
      persistence: 'localStorage+cookie',
      loaded: (ph) => {
        try {
          ph.register({ product: PRODUCT });
        } catch {
          /* fail-open */
        }
      },
    }) as unknown as PostHog;

    const companyKey = import.meta.env.VITE_POSTHOG_COMPANY_KEY;
    if (companyKey) {
      // Second, named instance for the company project. Shares the same proxy.
      company = posthog.init(
        companyKey,
        {
          api_host: '/ingest',
          ui_host: UI_HOST,
          capture_pageview: false,
          autocapture: false,
          loaded: (ph) => {
            try {
              ph.register({ product: PRODUCT });
            } catch {
              /* fail-open */
            }
          },
        },
        'company',
      ) as unknown as PostHog;
    }

    ready = true;
  } catch {
    // Fail-open: a bad init must not take down the app.
    product = null;
    company = null;
    ready = false;
  }
}

/** Manually record a pageview (call on SPA route change). Fail-open. */
export function capturePageview(path: string): void {
  if (!product) return;
  try {
    product.capture('$pageview', { $current_url: window.location.origin + path });
  } catch {
    /* fail-open */
  }
}

/**
 * Capture a product event. Top-line ★ events dual-send to the company project.
 * NO PII in `properties` — ids/counts/statuses/enums only.
 */
export function capture(event: string, properties?: Record<string, unknown>): void {
  if (!product) return;
  try {
    product.capture(event, { product: PRODUCT, ...(properties ?? {}) });
    if (company && TOP_LINE_EVENTS.has(event)) {
      company.capture(event, { product: PRODUCT, ...(properties ?? {}) });
    }
  } catch {
    /* fail-open */
  }
}

/** Identify the current user (person = app user id). NO PII in props. Fail-open. */
export function identify(userId: string, properties?: Record<string, unknown>): void {
  if (!product || !userId) return;
  try {
    product.identify(userId, properties);
    company?.identify(userId, properties);
  } catch {
    /* fail-open */
  }
}

/**
 * Associate the current user with their tenant (org) group. `name` is the one
 * allowed human-readable value and lives on the GROUP, not on event props.
 * Fail-open.
 */
export function groupTenant(orgId: string, name?: string): void {
  if (!product || !orgId) return;
  try {
    const props = name ? { name } : undefined;
    product.group('tenant', orgId, props);
    company?.group('tenant', orgId, props);
  } catch {
    /* fail-open */
  }
}

/** Reset identity on logout so the next user isn't merged. Fail-open. */
export function resetAnalytics(): void {
  if (!product) return;
  try {
    product.reset();
    company?.reset();
  } catch {
    /* fail-open */
  }
}
